import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, FileText, HandCoins, Trophy, RefreshCw } from "lucide-react";

const RANGE_OPTIONS = [
  { weeks: 4, label: "4w" },
  { weeks: 8, label: "8w" },
  { weeks: 12, label: "12w" },
  { weeks: 26, label: "26w" },
];

const SERIES = [
  { key: "applications", label: "Applications", color: "#ffffff", icon: FileText },
  { key: "offers", label: "Offers", color: "#3ac6a2", icon: HandCoins },
  { key: "awarded", label: "Awarded", color: "#f4b942", icon: Trophy },
];

export function AdminFunnelChart() {
  const [weeks, setWeeks] = useState(8);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (w = weeks) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/analytics/funnel?weeks=${w}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(weeks); }, [weeks]);

  const buckets = data?.weeks || [];
  const maxVal = Math.max(
    1,
    ...buckets.flatMap((b) => [b.applications, b.offers, b.awarded])
  );

  return (
    <div className="space-y-6" data-testid="admin-funnel">
      {/* Header + range toggle */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-emerald/15 border border-emerald/30 flex items-center justify-center">
            <TrendingUp className="h-4 w-4 text-emerald" strokeWidth={2.6} />
          </div>
          <div>
            <div className="font-display font-extrabold text-[22px] text-white tracking-[-0.03em] leading-none">Weekly Funnel</div>
            <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-white/45 mt-1.5">Applications → Offers → Awarded</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-1 p-1 bg-white/5 border border-white/10 rounded-full">
            {RANGE_OPTIONS.map((r) => (
              <button
                key={r.weeks}
                onClick={() => setWeeks(r.weeks)}
                data-testid={`funnel-range-${r.weeks}`}
                className={`px-3 py-1.5 rounded-full text-[12px] font-bold transition-colors ${
                  weeks === r.weeks ? "bg-white text-navy" : "text-white/60 hover:text-white"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => load(weeks)}
            data-testid="funnel-refresh"
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-white/15 bg-white/5 text-white/70 hover:text-white hover:bg-white/10"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={2.6} />
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="funnel-summary">
        <SummaryTile label="Applications" value={data?.totals?.applications ?? "—"} accent="text-white" dot="bg-white" />
        <SummaryTile label="Offers made" value={data?.totals?.offers ?? "—"} accent="text-emerald" dot="bg-emerald"
          sub={data ? `${data.totals.offer_rate}% offer rate` : ""} />
        <SummaryTile label="Deals awarded" value={data?.totals?.awarded ?? "—"} accent="text-amber" dot="bg-amber"
          sub={data ? `${data.totals.award_rate}% award rate` : ""} />
        <SummaryTile
          label="This week"
          value={buckets.length ? buckets[buckets.length - 1].applications : "—"}
          sub={buckets.length ? `${buckets[buckets.length - 1].awarded} awarded` : ""}
          accent="text-white" dot="bg-white/60"
        />
      </div>

      {/* Chart */}
      <div className="rounded-[20px] border border-white/10 bg-white/[0.02] p-6 md:p-8">
        {/* Legend */}
        <div className="flex flex-wrap items-center gap-5 mb-6">
          {SERIES.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.key} className="inline-flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: s.color }} />
                <Icon className="h-3.5 w-3.5 text-white/50" strokeWidth={2.4} />
                <span className="text-[12px] font-bold text-white/70">{s.label}</span>
              </div>
            );
          })}
        </div>

        {loading && !data ? (
          <div className="h-48 flex items-center justify-center text-white/40 text-sm font-semibold">Loading chart…</div>
        ) : buckets.length === 0 ? (
          <div className="h-48 flex items-center justify-center text-white/40 text-sm font-semibold">No data yet.</div>
        ) : (
          <div className="overflow-x-auto -mx-2 pb-2">
            <div className="flex items-end gap-4 md:gap-6 px-2 min-w-max h-[260px]">
              {buckets.map((b, idx) => (
                <div key={idx} className="flex flex-col items-center gap-3" data-testid={`funnel-week-${b.week_start}`}>
                  <div className="flex items-end gap-1.5 h-[210px]">
                    {SERIES.map((s) => {
                      const val = b[s.key] || 0;
                      const h = Math.round((val / maxVal) * 200);
                      return (
                        <div key={s.key} className="flex flex-col items-center justify-end w-6 md:w-7 group relative">
                          <div className="text-[10px] font-mono font-extrabold text-white/70 mb-1 h-3">
                            {val > 0 ? val : ""}
                          </div>
                          <div
                            className="w-full rounded-t-md transition-all duration-300 group-hover:opacity-80"
                            style={{
                              height: `${val > 0 ? Math.max(6, h) : 2}px`,
                              background: val > 0 ? s.color : "rgba(255,255,255,0.08)",
                              boxShadow: s.key === "offers" && val > 0 ? "0 0 20px rgba(58, 198, 162, 0.35)" : "none",
                            }}
                            title={`${s.label}: ${val}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className="font-mono text-[10.5px] uppercase tracking-[0.1em] font-bold text-white/45 whitespace-nowrap">
                    {b.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryTile({ label, value, sub, accent = "text-white", dot = "bg-white" }) {
  return (
    <div className="rounded-[20px] border border-white/10 bg-white/[0.02] p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-white/45">{label}</div>
        <div className={`h-2 w-2 rounded-full ${dot}`} />
      </div>
      <div className={`font-display font-extrabold text-[36px] leading-none tracking-[-0.04em] ${accent}`}>{value}</div>
      {sub ? <div className="mt-2 text-[11.5px] font-mono uppercase tracking-[0.1em] font-bold text-white/45">{sub}</div> : null}
    </div>
  );
}
